import { StyleSheet } from 'react-native';
import { reviewDesk } from '@/design-system';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
export const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
};

export const profileWebStyles = StyleSheet.create({
  stateCard: {
    width: '100%',
    minHeight: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 14,
  },
  stateIconDanger: {
    width: 70,
    height: 70,
    borderRadius: 35,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  stateTitle: {
    color: reviewDesk.ink,
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
  },
  stateButton: {
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  stateButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  logoutButton: {
    minHeight: 38,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.26)',
    backgroundColor: '#FFF7F7',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  logoutText: {
    color: COLORS.error,
    fontSize: 14,
    fontWeight: '800',
  },
  pageHeader: {
    marginBottom: 20,
  },
  pageTitle: {
    color: reviewDesk.ink,
    fontSize: 34,
    fontWeight: '800',
    letterSpacing: 0,
  },
  pageSubtitle: {
    color: reviewDesk.inkSoft,
    fontSize: 16,
    lineHeight: 23,
    marginTop: 8,
  },
  identityCard: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    borderRadius: 12,
    backgroundColor: reviewDesk.card,
    padding: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    marginBottom: 16,
  },
  identityCardTiny: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  avatar: {
    width: 74,
    height: 74,
    borderRadius: 37,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: reviewDesk.ink,
    fontSize: 24,
    fontWeight: '800',
  },
  identityInfo: {
    flex: 1,
    minWidth: 0,
  },
  identityName: {
    color: reviewDesk.ink,
    fontSize: 20,
    fontWeight: '800',
  },
  identityEmail: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    marginTop: 4,
  },
  identityMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  identityMeta: {
    color: reviewDesk.muted,
    fontSize: 12,
    fontWeight: '700',
  },
  metaDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: reviewDesk.lineStrong,
  },
  outlineButton: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  outlineButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  twoColumnGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  twoColumnGridCompact: {
    flexDirection: 'column',
    gap: 18,
    marginBottom: 18,
  },
  // `flex: 1` shares the row's width equally on desktop. Stacked in the
  // compact column it would split the grid's height instead (RN web: flex-basis
  // 0%, min-height 0), clipping the taller panel, so stacked panels size to
  // their content.
  panelStacked: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },
  panel: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 18,
    paddingBottom: 24,
    overflow: 'visible',
  },
  panelTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 14,
  },
  panelCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 14,
  },
  selectRow: {
    minHeight: 62,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  selectLeft: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  selectTitle: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  selectMeta: {
    color: reviewDesk.muted,
    fontSize: 12,
    marginTop: 3,
  },
  actionRow: {
    minHeight: 72,
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    overflow: 'visible',
  },
  actionRowDanger: {
    borderTopColor: 'rgba(239, 68, 68, 0.18)',
  },
  actionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionIconDanger: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  actionText: {
    flex: 1,
    minWidth: 0,
  },
  actionTitle: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '800',
  },
  actionTitleDanger: {
    color: COLORS.error,
  },
  actionSubtitle: {
    color: reviewDesk.muted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 3,
  },
  subscriptionCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 20,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 22,
    overflow: 'visible',
  },
  subscriptionCardCompact: {
    flexDirection: 'column',
    gap: 18,
  },
  planBlock: {
    flex: 1,
    flexDirection: 'row',
    gap: 14,
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: reviewDesk.line,
    paddingRight: 22,
  },
  // Stacked blocks size to their content: `flex: 1` / `flex: 1.2` in a column
  // split the card's height by ratio, so the plan buttons overlapped the usage
  // rows and the usage block got an empty tail (founder QA 2026-10-16).
  planBlockCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    alignItems: 'flex-start',
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    paddingRight: 0,
    paddingBottom: 16,
  },
  planBlockPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  planCopy: {
    flex: 1,
    minWidth: 0,
  },
  bigIcon: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  planLabel: {
    color: reviewDesk.muted,
    fontSize: 12,
    fontWeight: '700',
  },
  planTitle: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
    marginTop: 4,
  },
  planMeta: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    marginTop: 4,
    lineHeight: 18,
  },
  trialBadge: {
    alignSelf: 'flex-start',
    marginTop: 7,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#E8EEFF',
  },
  trialBadgeText: {
    color: reviewDesk.accent,
    fontSize: 11,
    fontWeight: '800',
  },
  trialNotice: {
    maxWidth: 370,
    marginTop: 5,
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 17,
  },
  planButtonRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  planButtonRowPhone: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  smallOutlineButton: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
    minHeight: 34,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    justifyContent: 'center',
    paddingHorizontal: 12,
    marginTop: 12,
  },
  smallOutlineButtonPhone: {
    width: '100%',
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  smallDangerButton: {
    borderColor: 'rgba(239, 68, 68, 0.35)',
    backgroundColor: 'rgba(239, 68, 68, 0.06)',
  },
  smallOutlineText: {
    flexShrink: 1,
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '800',
  },
  smallDangerText: {
    color: COLORS.error,
    fontSize: 13,
    fontWeight: '800',
  },
  usageBlock: {
    flex: 1.2,
    gap: 12,
    borderRightWidth: 1,
    borderRightColor: reviewDesk.line,
    paddingRight: 22,
  },
  usageBlockCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    paddingRight: 0,
    paddingBottom: 16,
  },
  usageMeter: {
    gap: 8,
    minWidth: 0,
  },
  usageMeterTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  usageIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  usageLabel: {
    color: reviewDesk.muted,
    fontSize: 12,
    fontWeight: '700',
  },
  usageValue: {
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
    marginTop: 2,
  },
  progressTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: reviewDesk.canvasMuted,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  creditBlock: {
    width: '100%',
    maxWidth: 220,
    alignItems: 'flex-start',
    gap: 5,
    flexShrink: 0,
  },
  creditBlockCompact: {
    width: '100%',
    maxWidth: '100%',
  },
  primaryButton: {
    minHeight: 44,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 14,
    marginBottom: 14,
  },
  primaryButtonPhone: {
    width: '100%',
  },
  disabledButton: {
    opacity: 0.65,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  warningBanner: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.3)',
    backgroundColor: '#FFF9EC',
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  warningBannerPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  warningText: {
    flex: 1,
    color: reviewDesk.inkSoft,
    fontSize: 14,
  },
  warningButton: {
    minHeight: 36,
    borderRadius: 8,
    backgroundColor: reviewDesk.amber,
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  warningButtonText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 13,
  },
});
